import ReactDOMServer from "react-dom/server";
import { formatPrice } from "@/src/helpers/map/commons";
import NewProjectBalloon from "../../balloon/new-project-balloon";
import NewListingBalloon from "../../balloon/new-listing-balloon";

export const getListingParams = (listingItem) => {
  const tradeType = listingItem?.tradeTypeName || listingItem?.tradeType?.name;
  const coordinates = listingItem.coordinates || listingItem?.location?.coordinate;
  const lat = coordinates?.lat;
  const lon = coordinates?.lon;
  const title = listingItem?.title || listingItem?.name;
  const price = listingItem?.priceDetail;
  const currency = listingItem?.price?.currency;
  const isOutstanding = listingItem.badges?.includes("promotion");
  const isProjectItem = listingItem.tags?.includes("proje");

  return { tradeType, lat, lon, title, price, currency, isOutstanding, isProjectItem };
};

export const getPlacemarkClass = (viewedListings, listingItem) => {
  let placemarkClassNames = "placemark";
  placemarkClassNames =
    viewedListings?.indexOf(listingItem.id) > -1
      ? placemarkClassNames.replace("placemark", "placemark visited")
      : placemarkClassNames;

  return placemarkClassNames;
};

export const createBalloons = ({ isProjectItem, listingItem }) => {
  let balloonContent = "";
  if (isProjectItem) {
    balloonContent = ReactDOMServer.renderToString(<NewProjectBalloon listing={listingItem} />);
  } else {
    balloonContent = ReactDOMServer.renderToString(<NewListingBalloon listing={listingItem} />);
  }
  return balloonContent;
};

export const getMarkerContent = (title, price, isProjectItem, tradeType, isOutstanding) => {
  if (isProjectItem) {
    return `${title} <span class="badge project-badge">Proje</span>`;
  }
  if (isOutstanding) {
    return `${formatPrice(price, tradeType)} <span class="badge">Öne Çıkan</span>`;
  }
  return `${formatPrice(price, tradeType)}`;
};

export const getIconContent = ({ placemarkClassNames, title, price, isProjectItem, tradeType, isOutstanding }) => {
  if (window.map.getZoom() > 15) {
    return `<div class="placemark ${placemarkClassNames}">${getMarkerContent(
      title,
      price,
      isProjectItem,
      tradeType,
      isOutstanding
    )}</div>`;
  } else {
    return `<div class="placemark ${placemarkClassNames}">${getMarkerContent(
      title,
      price,
      isProjectItem,
      tradeType,
      isOutstanding
    )}</div>`;
  }
};

const memoizedPlacemarkCache = {};

export const createPlacemark = ({ maps, lat, lon, iconContent, balloonContent, id }) => {
  const zoomedIn = window.map.getZoom() > 15;
  const key = `${lat}-${lon}`;
  if (!memoizedPlacemarkCache[key]) {
    const placemark = new maps.Placemark(
      [lat, lon],
      {
        iconContent: zoomedIn ? iconContent : "",
        balloonContentBody: balloonContent,
        id,
      },
      {
        iconLayout: zoomedIn ? "default#imageWithContent" : "default#image",
        iconImageSize: zoomedIn ? [100, 26] : [15, 15],
        iconImageOffset: [0, 0],
        iconImageHref: zoomedIn ? "" : "/assets/icons/mark-icon.svg",
        hideIconOnBalloonOpen: false,
        preset: "mark#icon",
        balloonAutoPan: false,
        balloonOffset: [0, 0],
      }
    );
    memoizedPlacemarkCache[key] = placemark;
  } else {
    const placemark = memoizedPlacemarkCache[key];

    placemark.options.set({
      iconLayout: zoomedIn ? "default#imageWithContent" : "default#image",
      iconImageSize: zoomedIn ? [100, 26] : [15, 15],
      iconImageHref: zoomedIn ? "" : "/assets/icons/mark-icon.svg",
    });

    placemark.properties.set({
      iconContent: zoomedIn ? iconContent : "",
    });
  }
  return memoizedPlacemarkCache[key];
};

export const getBalloonOffset = (placemark) => {
  const OFFSETS = {
    LEFT: 290,
    RIGHT: -280,
    TOP: 200,
    BOTTOM: 1,
    MIDDLE_Y: 85,
  };

  const bounds = window.map.getBounds();
  const [lat, lng] = placemark.geometry.getCoordinates();

  const left = bounds[0][1];
  const right = bounds[1][1];
  const top = bounds[1][0];
  const bottom = bounds[0][0];

  const sectionWidth = (right - left) / 3;
  const sectionHeight = (top - bottom) / 3;

  let offsetX = 0;
  if (lng <= left + sectionWidth) offsetX = OFFSETS.LEFT;
  else if (lng >= right - sectionWidth) offsetX = OFFSETS.RIGHT;

  let offsetY = 0;
  if (lat >= top - sectionHeight) offsetY = OFFSETS.TOP;
  else if (lat <= bottom + sectionHeight) offsetY = OFFSETS.BOTTOM;

  if (offsetY === 0 && offsetX !== 0) {
    offsetY = OFFSETS.MIDDLE_Y;
  }

  return [offsetX, offsetY];
};

export const getPlacemarkHoverStyle = (placemark) => {
  let newIconContent = "";
  const content = placemark.properties._data.iconContent;

  const [offsetX, offsetY] = getBalloonOffset(placemark);
  placemark.options.set("balloonOffset", [offsetX, offsetY]);

  if (content.includes("placemarkBefore")) return content;
  if (content.includes("visited")) {
    newIconContent = content.replace("placemark visited", "placemark mouseover");
  } else {
    newIconContent = content.replace("placemark", "placemark mouseover");
  }

  return newIconContent;
};

export const getPlacemarkNormalStyle = (placemark, id) => {
  const viewedListings = localStorage.getItem("viewedListings");
  const isViewedBefore = viewedListings?.indexOf(id) > -1;
  let newIconContent = "";
  const content = placemark.properties._data.iconContent;
  if (isViewedBefore) {
    newIconContent = content.replace("placemark mouseover", "placemark visited");
  } else {
    newIconContent = content.replace("placemark mouseover", "placemark");
  }
  return newIconContent;
};

export const getNewCriteria = async (location, criteria) => {
  const { type, id } = location;
  const clone = { ...criteria };
  let newCriteria = {};
  if (type === "region") {
    newCriteria = {
      ...clone,
      location: null,
      region: {
        key: "region",
        id,
      },
    };
  } else {
    const { city, district, town, locality, site } = location;
    newCriteria = {
      ...clone,
      location: {
        ...location,
        city,
        districts: district ? [district] : [],
        towns: town ? [town] : [],
        localities: locality ? [locality] : [],
        sites: site ? [site] : [],
      },
    };
  }
  return newCriteria;
};

export const decideLocationToFocus = (locations) => {
  const cities = locations?.city;
  const districts = locations?.district;
  const localities = locations?.locality;
  const towns = locations?.town;
  const pois = locations?.poi;

  if (pois?.length > 0) {
    return pois;
  }

  if (localities?.length > 0) {
    if (localities.length === 1) {
      return localities[0];
    }
    if (districts.length > 0) {
      return districts[0];
    }
  }

  if (towns?.length > 0) {
    if (towns.length === 1) {
      return towns[0];
    }
    if (districts.length > 0) {
      return districts[0];
    }
  }

  if (districts?.length > 0) {
    if (districts.length === 1) {
      return districts[0];
    }
    if (cities.length > 0) {
      return cities[0];
    }
  }

  if (cities?.length > 0) {
    return cities[0];
  }

  return locations[0];
};

export const getPoiBounds = (locations) => {
  const bounds = locations.map((location) => {
    if (Array.isArray(location.center)) {
      return [
        [location.center[1], location.center[0]],
        [location.center[1], location.center[0]],
      ];
    }
    return [
      [location.center.lat, location.center.lon],
      [location.center.lat, location.center.lon],
    ];
  });

  return bounds;
};

export const getParsedLocation = (selectedLocation) => {
  const { id, name, slug, type, district, city } = selectedLocation || {};
  const parentData = type === "town" ? district : city;
  const parentType = type === "town" ? "DISTRICT" : "CITY";
  const parent =
    type === "region"
      ? null
      : {
          id: parentData.id,
          type: parentType,
        };
  return {
    key: id,
    value: name,
    id,
    parent,
    name,
    slug,
    type,
    childs: null,
  };
};

export const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

export const removeBottomLeftTopRightFromPath = (path) => {
  const [basePath, queryString] = path.split("?");
  if (!queryString) return path;
  const params = queryString.split("&");
  const filteredParams = params.filter((param) => !param.startsWith("bottom_left=") && !param.startsWith("top_right="));
  const newQueryString = filteredParams.join("&");
  const newPath = newQueryString ? `${basePath}?${newQueryString}` : basePath;
  return newPath;
};
